import { useImagingResult } from '@hook'
import ImagingResultDocumentPreview from '@pages/Radiologist/ImagingResultDocumentPreview'
import type { ImagingOrderIncludedImagingResult } from '@type/imaging_order.type'
import { ModalHeader } from '@ui'
import { resolveApiAssetUrl } from '@util'
import { Box, Form, Layer, Spinner, Text } from 'grommet'
import moment from 'moment'
import type { FC } from 'react'

type Props = {
  isOpen?: boolean
  onClose?: () => void
  defaultData?: ImagingOrderIncludedImagingResult | null
  orderId?: string
  resultId?: string
}

const ImagingResultPreview: FC<Props> = ({ isOpen = true, onClose = () => {}, defaultData, orderId, resultId }) => {
  const shouldFetch = !defaultData && resultId
  const { imagingResult, isImagingResultLoading } = useImagingResult(orderId, resultId)

  const preview = defaultData ?? (imagingResult?.data as ImagingOrderIncludedImagingResult | undefined)

  const label = (r?: ImagingOrderIncludedImagingResult | null) => {
    if (!r) return ''
    const doc = r.attributes.document
    if (doc?.name) return doc.name
    if (r.attributes.study_name) return r.attributes.study_name
    const t = r.attributes.created_at
    return t ? `Imaging result · ${moment(t).format('MMM D, YYYY h:mm A')}` : `Imaging result · #${r.id}`
  }

  if (!isOpen) return null

  return (
    <Box>
      <Layer
        modal
        onEsc={onClose}
        position="center"
        style={{ borderColor: '#999999 !important', borderRadius: '15px' }}
      >
        <Form>
          <Box style={{ borderRadius: '2em', maxHeight: '90vh', overflowY: 'auto', width: '81vw' }}>
            <ModalHeader onClose={onClose} title={label(preview) || 'Imaging Result Preview'} />
            <Box pad={{ horizontal: 'large', vertical: 'medium' }}>
              {shouldFetch && isImagingResultLoading ? (
                <Box align="center" justify="center" pad="large">
                  <Spinner size="medium" /> Loading...
                </Box>
              ) : preview ? (
                <>
                  {preview.attributes.document ? (
                    <ImagingResultDocumentPreview document={preview.attributes.document} />
                  ) : preview.attributes.attachment ? (
                    <Box gap="small">
                      <Text size="14px" weight={500}>
                        {preview.attributes.attachment.filename ?? 'Attachment'}
                      </Text>
                      {preview.attributes.attachment.url ? (
                        <a
                          className="text-sm text-[#1565C0] underline"
                          href={resolveApiAssetUrl(preview.attributes.attachment.url)}
                          rel="noreferrer"
                          target="_blank"
                        >
                          Open attachment
                        </a>
                      ) : null}
                    </Box>
                  ) : preview.attributes.findings || preview.attributes.interpretation ? (
                    <Box gap="small">
                      {preview.attributes.findings ? (
                        <Text size="14px">Findings: {preview.attributes.findings}</Text>
                      ) : null}
                      {preview.attributes.interpretation ? (
                        <Text size="14px">Interpretation: {preview.attributes.interpretation}</Text>
                      ) : null}
                    </Box>
                  ) : (
                    <Text color="#666" size="small">
                      No preview data for this result.
                    </Text>
                  )}
                </>
              ) : (
                <Text>No imaging result found.</Text>
              )}
            </Box>
          </Box>
        </Form>
      </Layer>
    </Box>
  )
}

export default ImagingResultPreview
